import {
  Shield,
  BadgeCheck,
  Banknote,
  Building2,
  Layers,
  CalendarClock,
} from "lucide-react";
import { AreaPageShell } from "./AreaPageShell";

export interface CommercialFaq {
  question: string;
  answer: string;
}

export interface CommercialPageData {
  city: string;
  citySlug: string;
  serviceName: string;
  title: string;
  description: string;
  h1: string;
  heroIntro: string;
  climateText: string;
  localPresenceText: string;
  warrantyText: string;
  pricingText: string;
  priceFactorsText: string;
  financingText: string;
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  tpoText: string;
  epdmText: string;
  modifiedBitumenText: string;
  faqs: CommercialFaq[];
}

const buildSchema = (data: CommercialPageData) => {
  const base = "https://www.saskroofingcompany.com";
  const url = `${base}/areas/${data.citySlug}/commercial`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${base}/#localbusiness`,
    name: "Sask Roofing Company",
    url: `${base}/`,
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: data.city,
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    serviceType: "Commercial Roofing",
    name: "Commercial Roofing",
    description: data.description,
    url,
    provider: {
      "@id": `${base}/#localbusiness`,
      "@type": "RoofingContractor",
      name: "Sask Roofing Company",
      url: `${base}/`,
      telephone: "+1-639-739-7969",
    },
    areaServed: { "@type": "City", name: data.city },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "CAD",
      lowPrice: "8000",
      highPrice: "80000",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: `${base}/contact`,
      servicePhone: {
        "@type": "ContactPoint",
        telephone: "+1-639-739-7969",
        contactType: "customer service",
        areaServed: "CA-SK",
        availableLanguage: "English",
      },
    },
    termsOfService: `${base}/about#warranty`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "87",
      bestRating: "5",
      worstRating: "1",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${base}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: `${base}/areas`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.city,
        item: `${base}/areas/${data.citySlug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Commercial Roofing",
        item: url,
      },
    ],
  };

  return [breadcrumbSchema, localBusinessSchema, serviceSchema, faqSchema];
};

export function CommercialPage({ data }: { data: CommercialPageData }) {
  return (
    <AreaPageShell
      data={{
        ...data,
        schema: buildSchema(data),
        canonical: `/areas/${data.citySlug}/commercial`,
      }}
      heroImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/67bfca73-3cfe-4fd0-b9fd-9ed4f4780799.png&output=webp&q=60"
      heroAlt={`Commercial roofing in ${data.city}`}
      whyBlocks={[
        {
          icon: Building2,
          title: `Commercial flat-roof challenges in ${data.city}`,
          text: data.climateText,
        },
        {
          icon: Shield,
          title: `Commercial roofing presence in ${data.city}`,
          text: data.localPresenceText,
        },
        {
          icon: BadgeCheck,
          title: `Commercial roofing warranties in ${data.city}`,
          text: data.warrantyText,
        },
      ]}
      costBlocks={[
        {
          title: `Commercial roofing price per sq ft in ${data.city}`,
          text: data.pricingText,
        },
        {
          title: `What affects commercial roofing cost in ${data.city}`,
          text: data.priceFactorsText,
        },
        {
          title: `B2B financing & payment terms in ${data.city}`,
          text: data.financingText,
        },
      ]}
      extraCostTiles={[
        {
          label: "Per sq ft",
          value: "$8–$16",
          note: "commercial flat roof",
        },
        {
          label: "Billing",
          value: "B2B setup",
          note: "net-30 terms available",
        },
        {
          label: "Min size",
          value: "2,000 sq ft",
          note: "project minimum",
        },
      ]}
      extraCallout={{
        icon: CalendarClock,
        title: `Minimal-disruption commercial scheduling in ${data.city}`,
        text: "We schedule commercial work around your operating hours. Roofing projects can run nights, weekends, or off-hours so your business stays open while we work.",
      }}
      processSteps={[
        {
          title: `Commercial site assessment & roof inspection in ${data.city}`,
          text: data.step1,
        },
        {
          title: `Commercial system recommendation & bid in ${data.city}`,
          text: data.step2,
        },
        {
          title: `Scheduling with minimal business disruption in ${data.city}`,
          text: data.step3,
        },
        {
          title: `Commercial install, inspection & handover in ${data.city}`,
          text: data.step4,
        },
      ]}
      materialBlocks={[
        {
          icon: Layers,
          title: `TPO commercial roofing in ${data.city}`,
          text: data.tpoText,
        },
        {
          icon: Shield,
          title: `EPDM rubber roofing in ${data.city}`,
          text: data.epdmText,
        },
        {
          icon: Building2,
          title: `Modified bitumen & built-up roofing in ${data.city}`,
          text: data.modifiedBitumenText,
        },
      ]}
    />
  );
}
